import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import RecycleIcon from "@redotech/redo-icons/recycle.svg";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { useGiftReceiptRequestsCount } from "@redotech/redo-merchant-general-app/gift-receipt-requests/use-gift-receipt-requests-count";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { ViewTypesNames } from "@redotech/redo-views-common/views";
import { memo, useMemo } from "react";
import { OrdersReviewCountContext } from "../../services/orders/orders-review-count-service";
import { useInmarIntegrationQuery } from "../../setting/inmar-settings/hooks/use-inmar-integration-query";
import { useReturnsExperience } from "../experience/use-returns-experience";
import { CLAIMS_SOURCE } from "../record-home/sources/claims-source";
import { RETURNS_SOURCE } from "../record-home/sources/returns-source";
import { WARRANTIES_SOURCE } from "../record-home/sources/warranties-source";
import { recordListHref } from "../record-links/record-links";
import type { RecordViewCatalogSource } from "../views/use-record-view-catalog";
import {
  useRefreshWithdrawalCountOnNavigate,
  useWithdrawalNeedsActionCount,
} from "../withdrawals/use-withdrawal-needs-action-count";
import { RecordViewTabs, ViewTab } from "./record-view-tabs";

export type ViewTabsRecordType =
  | "returns"
  | "claims"
  | "warranties"
  | "recycle"
  | "withdrawals";

const RECYCLE_VIEWS: RecordViewCatalogSource = {
  type: "recycle",
  recordType: ReturnTypeEnum.RECYCLE,
  Icon: RecycleIcon,
  copy: (t) => ({ recordsLabel: t("Recycle"), allLabel: t("All recycle") }),
  views: { viewType: ViewTypesNames.RECYCLE, counted: true },
  quickViews: [],
  allHref: (routes) =>
    recordListHref(routes.returnsBaseUrl, ReturnTypeEnum.RECYCLE),
};

/** In the new returns experience, a type's tables show its views as tabs in place of the side nav. */
export const RecordViewTabsGate = memo(function RecordViewTabsGate({
  type,
}: {
  type: ViewTabsRecordType;
}) {
  const { useNewUi } = useReturnsExperience();
  if (!useNewUi) {
    return null;
  }
  switch (type) {
    case "returns":
      return <ReturnsViewTabs />;
    case "claims":
      return <ClaimsViewTabs />;
    case "warranties":
      return <WarrantiesViewTabs />;
    case "recycle":
      return <RecycleViewTabs />;
    case "withdrawals":
      return <WithdrawalsViewTabs />;
  }
});

function present(count: number | undefined): number | undefined {
  return count !== undefined && count > 0 ? count : undefined;
}

const ReturnsViewTabs = memo(function ReturnsViewTabs() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;
  const giftReceiptRequestsCount = useGiftReceiptRequestsCount();
  const inmarEnabled =
    !!useInmarIntegrationQuery().data?.inmarIntegration?.enabled;
  const instantExchangeEnabled = team.settings.exchanges.allowInstant;

  const otherTabs = useMemo(
    (): ViewTab[] => [
      ...(giftReceiptsEnabled
        ? [
            {
              key: "returns:gift-receipt-requests",
              label: t("Gift receipt requests"),
              href: routes.getReturnsSubRoute("gift-receipt-requests"),
              count: present(giftReceiptRequestsCount),
            },
          ]
        : []),
      ...(inmarEnabled
        ? [
            {
              key: "returns:inmar-blind-returns",
              label: t("{{providerName}} blind returns", {
                providerName: "Inmar",
              }),
              href: routes.getReturnsSubRoute("inmar-blind-returns"),
            },
          ]
        : []),
      ...(instantExchangeEnabled
        ? [
            {
              key: "returns:instant-exchange",
              label: t("Instant exchange"),
              href: routes.getReturnsSubRoute("instant-exchange"),
            },
          ]
        : []),
    ],
    [
      t,
      routes,
      giftReceiptsEnabled,
      giftReceiptRequestsCount,
      inmarEnabled,
      instantExchangeEnabled,
    ],
  );

  return (
    <RecordViewTabs
      createViewHref={routes.getReturnsSubRoute("create")}
      otherTabs={otherTabs}
      source={RETURNS_SOURCE}
      title={t("Returns")}
    />
  );
});

const ClaimsViewTabs = memo(function ClaimsViewTabs() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const omsEnabled = useCapability("oms");
  const packageProtectionEnabled = useCapability("packageProtection");
  const isRedoAdmin = useIsRedoAdmin();
  const canManagePackageProtectionClaims = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
  );
  const canManageCarrierClaims = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
  );
  const { roClaimsNeedingReview } = useRequiredContext(
    OrdersReviewCountContext,
  );

  const showClaimViews =
    packageProtectionEnabled && canManagePackageProtectionClaims;
  const showCarrierClaims =
    (omsEnabled || !!team.settings.returns?.enabled) && canManageCarrierClaims;
  const showManagedClaims =
    !!team.settings.packageProtection?.newClaimsTables &&
    !!team.settings.packageProtection.packageProtectionPlusEnabled &&
    canManagePackageProtectionClaims &&
    isRedoAdmin;

  const otherTabs = useMemo(
    (): ViewTab[] => [
      ...(showCarrierClaims
        ? [
            {
              key: "claims:carrier-claims",
              label: t("Carrier claims"),
              href: routes.getClaimsSubRoute("carrier-claims"),
            },
          ]
        : []),
      ...(showManagedClaims
        ? [
            {
              key: "claims:managed",
              label: t("Managed"),
              href: routes.getClaimsSubRoute("managed"),
              count: present(roClaimsNeedingReview),
            },
          ]
        : []),
    ],
    [t, routes, showCarrierClaims, showManagedClaims, roClaimsNeedingReview],
  );

  const copy = CLAIMS_SOURCE.copy(t);
  return (
    <RecordViewTabs
      createViewHref={
        showClaimViews ? routes.getClaimsSubRoute("create") : undefined
      }
      otherTabs={otherTabs}
      section={copy.recordsLabel}
      source={showClaimViews ? CLAIMS_SOURCE : undefined}
      title={copy.allLabel}
    />
  );
});

const WarrantiesViewTabs = memo(function WarrantiesViewTabs() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const warrantiesEnabled = useCapability("warranties");
  const registrationEnabled = !!team.settings.warranties?.registrationEnabled;

  const otherTabs = useMemo(
    (): ViewTab[] =>
      registrationEnabled
        ? [
            {
              key: "warranties:registrations",
              label: t("Registrations"),
              href: routes.registrationsHref,
            },
          ]
        : [],
    [t, routes, registrationEnabled],
  );
  const allAliases = useMemo(
    () => [routes.warrantiesHref],
    [routes.warrantiesHref],
  );

  const copy = WARRANTIES_SOURCE.copy(t);
  return (
    <RecordViewTabs
      allAliases={allAliases}
      createViewHref={
        warrantiesEnabled ? routes.getWarrantiesSubRoute("create") : undefined
      }
      otherTabs={otherTabs}
      section={copy.recordsLabel}
      source={warrantiesEnabled ? WARRANTIES_SOURCE : undefined}
      title={copy.allLabel}
    />
  );
});

const RecycleViewTabs = memo(function RecycleViewTabs() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const allAliases = useMemo(() => [routes.recycleHref], [routes.recycleHref]);

  const copy = RECYCLE_VIEWS.copy(t);
  return (
    <RecordViewTabs
      allAliases={allAliases}
      createViewHref={routes.getRecycleSubRoute("create")}
      section={copy.recordsLabel}
      source={RECYCLE_VIEWS}
      title={copy.allLabel}
    />
  );
});

const WithdrawalsViewTabs = memo(function WithdrawalsViewTabs() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const needsActionCount = useWithdrawalNeedsActionCount();
  useRefreshWithdrawalCountOnNavigate();

  const tabs = useMemo(
    (): ViewTab[] => [
      {
        key: "withdrawals:all",
        label: t("All withdrawals"),
        href: recordListHref(routes.returnsBaseUrl, ReturnTypeEnum.WITHDRAWAL),
      },
      {
        key: "withdrawals:needs-action",
        label: t("Needs action"),
        href: routes.getWithdrawalSubRoute("needs-action"),
        count: present(needsActionCount),
      },
    ],
    [t, routes, needsActionCount],
  );

  return (
    <RecordViewTabs
      otherTabs={tabs}
      section={t("Withdrawals")}
      title={t("All withdrawals")}
    />
  );
});
